import styled from 'styled-components';

import style from '../../../style';
import type { Color, Shadow, Size as ThemeSize } from '../../../style/types/theme';
import { Size, Variant } from './types';

interface WrapperProps {
  $size: Size;
  $variant: Variant;
  $isHovered?: boolean;
  $disabled?: boolean;
  $disabledWithoutShadow?: boolean;
}

interface IconProps {
  $variant: Variant;
  $disabled?: boolean;
}

const getWrapperSize = (size: Size, themeSize: ThemeSize) => {
  switch (size) {
    case Size.SMALL:
      return themeSize.units(4);
    case Size.BIG:
    default:
      return themeSize.units(5);
  }
};

const getBackgroundColor = ({
  variant,
  themeColor,
  $disabled,
}: {
  variant: Variant;
  themeColor: Color;
  $disabled?: boolean;
}) => {
  switch (variant) {
    case Variant.PRIMARY:
      return $disabled
        ? themeColor.background.reversed.disabled()
        : themeColor.background.reversed.default();
    case Variant.NEUTRAL:
    default:
      return $disabled
        ? themeColor.background.primary.disabled()
        : themeColor.background.primary.default();
  }
};

const getBackgroundColorHover = (variant: Variant, themeColor: Color) => {
  switch (variant) {
    case Variant.PRIMARY:
      return themeColor.background.reversed.hover();
    case Variant.NEUTRAL:
    default:
      return themeColor.background.primary.hover();
  }
};

const getBackgroundShadow = ({
  $variant,
  $disabled,
  $disabledWithoutShadow,
  shadow,
}: {
  $variant: Variant;
  $disabled?: boolean;
  $disabledWithoutShadow?: boolean;
  shadow: Shadow;
}) => {
  if ($disabledWithoutShadow) {
    return 'none';
  }

  if ($disabled || $variant === Variant.NEUTRAL) {
    return shadow.short;
  }

  return 'none';
};

const getIconColor = ({
  variant,
  themeColor,
  $disabled,
}: {
  variant: Variant;
  themeColor: Color;
  $disabled?: boolean;
}) => {
  switch (variant) {
    case Variant.PRIMARY:
      return $disabled
        ? themeColor.content.reversed.disabled()
        : themeColor.content.reversed.default();
    case Variant.NEUTRAL:
    default:
      return $disabled
        ? themeColor.content.secondary.disabled()
        : themeColor.content.secondary.default();
  }
};

export const StyledIcon = styled.i<IconProps>(
  ({ theme: { size, color }, $variant, $disabled }) => ({
    width: size.units(2),
    height: size.units(2),
    color: getIconColor({ variant: $variant, themeColor: color, $disabled }),
    transition: 'color .15s cubic-bezier(.4,0,.4,1)',
  }),
);

export const Background = styled.div<WrapperProps>(
  ({
    theme: { size, color, shadow },
    $size,
    $variant,
    $disabled,
    $disabledWithoutShadow,
  }) => ({
    width: getWrapperSize($size, size),
    height: getWrapperSize($size, size),
    background: getBackgroundColor({
      variant: $variant,
      themeColor: color,
      $disabled,
    }),
    borderRadius: '50%',
    boxShadow: getBackgroundShadow({
      $variant,
      $disabled,
      $disabledWithoutShadow,
      shadow,
    }),
    cursor: $disabled ? 'not-allowed' : 'pointer',
    transition:
      'background .15s cubic-bezier(.4,0,.4,1), transform .1s cubic-bezier(.4,0,.4,1), box-shadow .1s cubic-bezier(.4,0,.4,1)',
  }),
);

export const Wrapper = styled.button<WrapperProps>(
  ({ theme: { color, size }, $variant, $size, $isHovered, $disabled }) => ({
    position: 'relative',
    width: getWrapperSize($size, size),
    height: getWrapperSize($size, size),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: $disabled ? 'not-allowed' : 'pointer',

    ...(!$disabled &&
      style.mixins.devices.desktop({
        '&:hover': {
          [Background]: {
            background: getBackgroundColorHover($variant, color),
          },

          [StyledIcon]: {
            color:
              $variant === Variant.NEUTRAL ? color.content.primary.default() : undefined,
          },
        },

        '&:active': {
          [Background]: {
            transform: 'scale(.95)',
            boxShadow: `0px 0px 0px ${color.shadow.primary.default(0.2)}`,
          },
        },
      })),

    ...($isHovered &&
      !$disabled &&
      style.mixins.increaseSpecificity({
        [Background]: {
          background: getBackgroundColorHover($variant, color),
        },

        [StyledIcon]: {
          color:
            $variant === Variant.NEUTRAL ? color.content.primary.default() : undefined,
        },
      })),

    [StyledIcon]: {
      zIndex: 1,
    },

    [Background]: {
      position: 'absolute',
    },
  }),
);
